import React, { useContext, useEffect, useMemo, useState } from 'react';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import moment from 'moment';

import DashboardContextMenu from './DashboardContextMenu';
import DashboardContextMenuContext from '../../contexts/DashboardContextMenuContext';
import MenuPosition from './MenuPositioner';
import GridProvider from '../../contextProviders/GridProvider';
import PopupGrid from '../PopupGrid';
import buildFilterInput from '../../utils/buildFilterInput';
import mergeFilterInputs from '../../mergeFilterInputs';
import useDateScope, { useBuildDateScope } from '../../hooks/useDateScope';
import { usePlatesToFilterInput } from 'screens/GoalShow/useGoalData';
import MetricOptionsContext from '../../contexts/MetricOptionsContext';
import isDefined from '../../isDefined';
import metricTypeCheckers from '../../types/metricTypeCheckers';
import filterTypeCheckers from '../FilterPlateForm/filterTypeCheckers';

const useMetrics = (metricIds: string[]) => {
  const { metricOptions, normalMetrics } = useContext(MetricOptionsContext);
  const [metrics, setMetrics] = useState<
    (Metrics.NormalMetric | Metrics.SpecialMetric)[]
  >([]);

  useEffect(() => {
    const foundMetrics = metricIds
      .map((mid) => metricOptions.find((mo) => mo.id === mid))
      .filter(isDefined);

    const newMetrics = [] as (Metrics.NormalMetric | Metrics.SpecialMetric)[];
    const basicMetrics = foundMetrics.filter(metricTypeCheckers.isNormalMetric);
    newMetrics.push(...basicMetrics);
    const specialMetrics = foundMetrics.filter(
      metricTypeCheckers.isSpecialMetric,
    );
    newMetrics.push(...specialMetrics);
    const compoundMetrics = foundMetrics.filter(
      metricTypeCheckers.isCompoundMetric,
    );
    compoundMetrics.forEach((cm) => {
      const metricsInCompound = normalMetrics.filter((m) =>
        cm.metricIds.includes(m.id),
      );

      newMetrics.push(...metricsInCompound);
    });

    setMetrics(newMetrics);
  }, [metricIds, metricOptions, normalMetrics]);

  return { metrics };
};

const applyDrillTerm = (
  metricFilters: FilterInput,
  drillDown: FixedFilter,
): FilterInput => {
  const drillDownFilter = buildFilterInput({
    scopes: [],
    drillDowns: [drillDown],
  });
  const hasConflict = (metricFilters.keywords || []).some(
    (k) => k.field === drillDown.field,
  );
  if (hasConflict) {
    const withoutConflict = {
      ...metricFilters,
      keywords: (metricFilters.keywords || []).filter(
        (k) => k.field !== drillDown.field,
      ),
    };
    return mergeFilterInputs(withoutConflict, drillDownFilter);
  } else {
    return mergeFilterInputs(metricFilters, drillDownFilter);
  }
};

const buildDateScopeOverride = (
  drillDown: FixedFilter,
  dateField?: string,
): DateRangeInput | undefined => {
  if (
    !filterTypeCheckers.isDateFilter(drillDown) ||
    !drillDown.dateRangeValues
  ) {
    return undefined;
  }

  return {
    ...drillDown.dateRangeValues[0].value,
    dateField: dateField ? dateField : 'date',
  };
};

const toLabel = (drillDown: FixedFilter): string => {
  if (filterTypeCheckers.isTextFilter(drillDown)) {
    if (drillDown.keywordValues[0].length === 0) {
      return '';
    }

    return drillDown.keywordValues[0];
  }

  if (filterTypeCheckers.isDateFilter(drillDown)) {
    if (!drillDown.dateRangeValues || drillDown.dateRangeValues.length === 0) {
      return '';
    }

    return drillDown.dateRangeValues[0].label;
  }

  if (filterTypeCheckers.isNumberFilter(drillDown)) {
    if (!drillDown.rangeValue) {
      return '';
    }

    if (drillDown.rangeValue.valueB) {
      return `${drillDown.rangeValue.operator} ${drillDown.rangeValue.valueA} and ${drillDown.rangeValue.valueB}`;
    }
    return `${drillDown.rangeValue.operator} ${drillDown.rangeValue.valueA}`;
  }

  if (filterTypeCheckers.isBooleanFilter(drillDown)) {
    return `${drillDown.field} is ${drillDown.booleanValue}`;
  }

  return '';
};

// A date drill on a series fetched for an earlier period: its points sit on
// the chart's dates (see alignGroupResponses), so back to the period's own
const shiftDateRange = ({
  range,
  days,
}: {
  range: DateRangeInput;
  days: number;
}): DateRangeInput => {
  const shift = (date?: string) =>
    date
      ? moment.utc(date).subtract(days, 'days').format('YYYY-MM-DD')
      : undefined;
  return {
    ...range,
    startDate: shift(range.startDate),
    endDate: shift(range.endDate),
  };
};

const DashboardContextMenuContainer = ({
  metricIds,
  drillDown,
  queryOverrides,
}: {
  metricIds: string[];
  drillDown: FixedFilter;
  // The clicked series' own question (see V5SeriesQueryOverrides)
  queryOverrides?: V5SeriesQueryOverrides;
}) => {
  const { dateField } = useDateScope({});
  const buildDateScope = useBuildDateScope({});
  const platesToFilterInput = usePlatesToFilterInput();
  const { selectedMetric, setSelectedMetric, closeMenu } = useContext(
    DashboardContextMenuContext,
  );

  const { metrics } = useMetrics(metricIds);
  const metricFiltering = useMemo(() => {
    if (!selectedMetric) {
      return;
    }

    const filtered = applyDrillTerm(
      metricTypeCheckers.isSpecialMetric(selectedMetric)
        ? window.emptyObject
        : selectedMetric.filters,
      drillDown,
    );
    const seriesFilters = queryOverrides ? queryOverrides.filters : undefined;
    return seriesFilters && seriesFilters.length > 0
      ? mergeFilterInputs(filtered, platesToFilterInput(seriesFilters))
      : filtered;
  }, [drillDown, platesToFilterInput, queryOverrides, selectedMetric]);

  const dateScopeOverride = useMemo(() => {
    const offset =
      queryOverrides &&
      queryOverrides.dateOffset &&
      queryOverrides.dateOffset.count > 0
        ? queryOverrides.dateOffset
        : undefined;
    const seriesDateField = queryOverrides
      ? queryOverrides.dateField
      : undefined;
    const drillRange = buildDateScopeOverride(
      drillDown,
      seriesDateField || dateField,
    );
    if (!offset) {
      return (
        drillRange ??
        (seriesDateField
          ? buildDateScope({ dateField: seriesDateField })
          : undefined)
      );
    }
    const offsetScope = buildDateScope({
      comparison: { compareType: offset.type },
      comparisonSteps: offset.count,
      dateField: seriesDateField,
    });
    if (!drillRange) {
      return offsetScope;
    }
    const currentScope = buildDateScope({});
    const days =
      currentScope.startDate && offsetScope.startDate
        ? moment
            .utc(currentScope.startDate)
            .diff(moment.utc(offsetScope.startDate), 'days')
        : 0;
    return shiftDateRange({ range: drillRange, days });
  }, [buildDateScope, dateField, drillDown, queryOverrides]);

  return (
    <React.Fragment>
      {!selectedMetric && (
        <MenuPosition closeMenu={closeMenu}>
          <DashboardContextMenu
            metrics={metrics}
            label={toLabel(drillDown)}
            setSelectedMetric={setSelectedMetric}
          />
        </MenuPosition>
      )}
      <ModalTransition>
        {selectedMetric && (
          <Modal
            width={'80vw'}
            height={'90vh'}
            onClose={closeMenu}
            shouldScrollInViewport={false}
            autoFocus={false}
          >
            <GridProvider
              dataType={selectedMetric.dataType}
              metricFiltering={metricFiltering}
              dateScopeOverride={dateScopeOverride}
            >
              <PopupGrid close={closeMenu} />
            </GridProvider>
          </Modal>
        )}
      </ModalTransition>
    </React.Fragment>
  );
};

const Gate = () => {
  const { isOpen, drillDown, metricIds, queryOverrides } = useContext(
    DashboardContextMenuContext,
  );

  if (isOpen && drillDown && metricIds) {
    return (
      <DashboardContextMenuContainer
        metricIds={metricIds}
        drillDown={drillDown}
        queryOverrides={queryOverrides}
      />
    );
  }
  return null;
};

export default Gate;
